import React from "react";
import { Helmet } from "react-helmet";
import { DEFAULT_OG_IMAGE } from "./SEO";

const BASE_URL = "https://ezcarrentals.com.au";

const businessAddress = {
  "@type": "PostalAddress",
  streetAddress: "33B Clifford Street",
  addressLocality: "Huntingdale",
  addressRegion: "VIC",
  postalCode: "3166",
  addressCountry: "AU",
};

const businessGeo = {
  "@type": "GeoCoordinates",
  latitude: -37.875783,
  longitude: 145.100611,
};

const StructuredData = ({
  type = "localBusiness",
  suburb = null,
  breadcrumbs = null,
  article = null,
  faqItems = null,
  blogList = null,
}) => {
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${BASE_URL}/#organization`,
    name: "EZ Car Rentals",
    legalName: "EZ MOTOR GROUP PTY LTD TRADING AS EZ Car Rentals",
    alternateName: "EZ Car Rentals Melbourne",
    url: BASE_URL,
    logo: `${BASE_URL}/favicon-icon.png`,
    image: DEFAULT_OG_IMAGE,
    description:
      "Affordable car, van, and ute rental services in Melbourne, Victoria, Australia. EZ Car Rentals offers reliable vehicle hire in Huntingdale, Chadstone, and surrounding areas.",
    address: businessAddress,
    geo: businessGeo,
    telephone: "+61400920512",
    email: "info@ezcarrentals.com.au",
    priceRange: "$$",
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday",
        ],
        opens: "08:00",
        closes: "20:00",
      },
    ],
    areaServed: [
      {
        "@type": "City",
        name: "Melbourne",
        containedIn: { "@type": "State", name: "Victoria" },
      },
      { "@type": "State", name: "Victoria" },
      { "@type": "Country", name: "Australia" },
    ],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Vehicle Rental Services",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Car Rental",
            description: "Affordable car rental in Melbourne, Victoria",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Van Hire",
            description: "Van rental services in Melbourne and surrounding areas",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Ute Hire",
            description: "Ute rental for commercial and personal use in Victoria",
          },
        },
      ],
    },
    sameAs: [
      "https://www.facebook.com/EZCarRentalsMelbourne",
      "https://www.instagram.com/ezcarrentals_aus/",
    ],
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${BASE_URL}/#organization`,
    name: "EZ Car Rentals",
    legalName: "EZ MOTOR GROUP PTY LTD TRADING AS EZ Car Rentals",
    url: BASE_URL,
    logo: `${BASE_URL}/favicon-icon.png`,
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+61400920512",
      contactType: "Customer Service",
      email: "info@ezcarrentals.com.au",
      areaServed: "AU",
      availableLanguage: "English",
    },
    address: businessAddress,
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "EZ Car Rentals",
    url: BASE_URL,
  };

  const getSuburbLocalBusinessSchema = () => ({
    "@context": "https://schema.org",
    "@type": "AutoRental",
    "@id": `${BASE_URL}/car-rental-${suburb.slug}/#business`,
    name: `EZ Car Rentals – Car Hire ${suburb.name}`,
    url: `${BASE_URL}/car-rental-${suburb.slug}`,
    image: DEFAULT_OG_IMAGE,
    telephone: "+61400920512",
    email: "info@ezcarrentals.com.au",
    address: businessAddress,
    geo: businessGeo,
    parentOrganization: {
      "@type": "Organization",
      name: "EZ Car Rentals",
      url: BASE_URL,
    },
    areaServed: {
      "@type": "City",
      name: suburb.name,
      containedIn: { "@type": "State", name: "Victoria" },
    },
    makesOffer: {
      "@type": "Offer",
      itemOffered: {
        "@type": "Service",
        name: `Car rental in ${suburb.name}`,
        description: `Affordable car, van and ute hire near ${suburb.name}, Melbourne.`,
      },
    },
  });

  const getBreadcrumbSchema = (items) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url.startsWith("http") ? item.url : `${BASE_URL}${item.url}`,
    })),
  });

  const getArticleSchema = (post) => ({
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.description,
    image: post.image || DEFAULT_OG_IMAGE,
    datePublished: post.datePublished,
    dateModified: post.dateModified || post.datePublished,
    author: {
      "@type": "Organization",
      name: "EZ Car Rentals",
      url: BASE_URL,
    },
    publisher: {
      "@type": "Organization",
      name: "EZ Car Rentals",
      logo: {
        "@type": "ImageObject",
        url: `${BASE_URL}/favicon-icon.png`,
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": post.url,
    },
  });

  const getFaqSchema = (items) => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  });

  const getBlogListSchema = (posts) => ({
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: posts.map((post, index) => ({
      "@type": "ListItem",
      position: index + 1,
      url: post.url,
      name: post.title,
    })),
  });

  const getSchema = () => {
    if (type === "suburbLocalBusiness" && suburb) {
      return getSuburbLocalBusinessSchema();
    }
    if (type === "breadcrumb" && breadcrumbs) {
      return getBreadcrumbSchema(breadcrumbs);
    }
    if (type === "article" && article) {
      return getArticleSchema(article);
    }
    if (type === "faq" && faqItems) {
      return getFaqSchema(faqItems);
    }
    if (type === "blogList" && blogList) {
      return getBlogListSchema(blogList);
    }
    switch (type) {
      case "localBusiness":
        return localBusinessSchema;
      case "organization":
        return organizationSchema;
      case "website":
        return websiteSchema;
      default:
        return localBusinessSchema;
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(getSchema())}</script>
    </Helmet>
  );
};

export default StructuredData;
